/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  Signal,
  ViewChild
} from '@angular/core';
import { OptNode } from '../opt-node';
import { OptionHoverService } from '../option-hover.service';
import { OptionsRootHolder } from '../options-root-holder';
import { OptionSelectionService } from '../selection/option-selection.service';
import { SelectionEvent } from '../selection/selection-event';
import { Option } from './option';

let nextUniqueId = 0;

@Component({
    selector: 'ngf-option',
    templateUrl: './option.component.html',
    styleUrls: ['./option.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        { provide: OptNode, useExisting: OptionComponent }
    ],
    standalone: false
})
export class OptionComponent extends Option implements AfterViewInit {

  private readonly service = inject(OptionSelectionService);
  private readonly hoverService = inject(OptionHoverService);
  private readonly rootHolder = inject(OptionsRootHolder, { optional: true });
  readonly parent = inject(OptNode, { skipSelf: true, optional: true });
  readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  @Input()
  value: any;
  @Input()
  disabled = false;
  @Input() id?: string;
  @Input() ariaLabel?: string;

  @ViewChild('content', {static: true})
  content: ElementRef<HTMLElement>;

  private readonly fallbackOptionId = `ngf-option-${nextUniqueId++}`;
  readonly selected: Signal<boolean> = this.service.isSelected(this);
  readonly hovered: Signal<boolean> = this.hoverService.isHovered(this);

  get optionId(): string {
    // Resolved lazily so a consumer-supplied [id] @Input (bound after construction)
    // is respected. Falls back to the generated id (counter incremented once per instance).
    return this.id || this.fallbackOptionId;
  }

  textLabel: string;

  ngAfterViewInit() {
    this.textLabel = this.ariaLabel ?? this.textContent;
  }

  get textContent(): string {
    return this.content.nativeElement.textContent.trim();
  }

  // Fold the selection state into the option's accessible name for multi-select listboxes.
  // Why: with the aria-activedescendant pattern, screen readers (especially JAWS+Chrome and
  // NVDA+Firefox) don't reliably re-announce when aria-selected flips on the currently-active
  // descendant. Putting the state in aria-label means every arrow-key navigation announces
  // "<label>, selected" or "<label>, not selected" regardless of how the SR handles aria-selected.
  get effectiveLabel(): string | null {
    if (!this.textLabel) { return null; }
    if (!this.isMultiselect) { return this.textLabel; }
    return `${this.textLabel}, ${this.selected() ? 'selected' : 'not selected'}`;
  }

  private get isMultiselect(): boolean {
    try {
      return !!this.rootHolder?.get()?.multiselect;
    } catch {
      return false;
    }
  }

  @HostListener('click')
  handleClick() {
    if (this.disabled) {
      return;
    }
    const event: SelectionEvent = {value: this.value, userInput: true};
    this.service.select(event);
  }

  @HostListener('mousedown', ['$event'])
  preventFocusLose(event: MouseEvent) {
    event.preventDefault();
  }

  @HostListener('mouseenter')
  handleMouseEnter() {
    if (this.disabled) { return; }
    this.hoverService.hover(this);
  }

}
